import { saveProject } from './database';
import { storePhotoBlob } from './photo.js';
import { storeDocumentBlob } from './document-blob-store';
import { storeVideoBlob } from './video-blob-store';
import { getStage1VideoProcessingMode } from './stage1-video-policy';
import {
    buildVideoStoragePolicy,
    persistVideoOriginalInIndexedDbIfAllowed,
} from './video-storage-policy';

const defaultDependencies = Object.freeze({
    saveProject,
    storePhotoBlob,
    storeDocumentBlob,
    storeVideoBlob,
});

function restoredVideoSourceSize(video: any, videoEntry: any): number {
    const candidates = [
        videoEntry?.blob?.size,
        videoEntry?.meta?.size,
        videoEntry?.meta?.originalSize,
        video?.size,
        video?.originalSize,
    ]
        .map(value => Number(value))
        .filter(value => Number.isSafeInteger(value) && value >= 0);
    return candidates.length > 0 ? Math.max(...candidates) : 0;
}

/**
 * Persist a parsed sync-folder restore as one durable project.
 * Media bytes are stored first and their generated IDs are stamped onto the
 * project envelope before the API save, so a successful return is reload-safe.
 */
export async function persistRestoredFolderProject(restored: any, dependencies: any = {}) {
    if (!restored?.project?.id) {
        throw new Error('The restored project is missing a valid project ID.');
    }

    const deps = { ...defaultDependencies, ...dependencies };
    const project = { ...restored.project };
    const restoredPhotos = Array.isArray(project.photos) ? [...project.photos] : [];

    for (const photoEntry of restored.photoBlobs || []) {
        const index = Number(photoEntry?.index);
        if (!Number.isInteger(index) || !restoredPhotos[index]) {
            throw new Error(`Restored photo mapping ${String(photoEntry?.index)} is invalid.`);
        }
        if (!(photoEntry?.blob instanceof Blob)) {
            throw new Error(`Restored photo ${index + 1} has no readable file bytes.`);
        }
        const blobId = await deps.storePhotoBlob(photoEntry.blob, project.id, {
            ...(photoEntry.meta || {}),
            restoredFromSyncFolder: true,
        });
        if (!blobId) throw new Error(`Restored photo ${index + 1} could not be stored.`);
        restoredPhotos[index] = { ...restoredPhotos[index], blobId };
    }
    project.photos = restoredPhotos;

    const restoredDocuments = Array.isArray(project.documents) ? [...project.documents] : [];
    for (const documentEntry of restored.documentBlobs || []) {
        const index = Number(documentEntry?.index);
        if (!Number.isInteger(index) || !restoredDocuments[index]) {
            throw new Error(`Restored document mapping ${String(documentEntry?.index)} is invalid.`);
        }
        if (!(documentEntry?.blob instanceof Blob)) {
            throw new Error(`Restored document ${index + 1} has no readable file bytes.`);
        }
        const docBlobId = await deps.storeDocumentBlob(documentEntry.blob, project.id, {
            ...(documentEntry.meta || {}),
            restoredFromSyncFolder: true,
        });
        if (!docBlobId) throw new Error(`Restored document ${index + 1} could not be stored.`);
        restoredDocuments[index] = { ...restoredDocuments[index], docBlobId };
    }
    project.documents = restoredDocuments;

    const restoredVideos = Array.isArray(project.videos) ? [...project.videos] : [];
    for (const videoEntry of restored.videoBlobs || []) {
        const index = Number(videoEntry?.index);
        if (!Number.isInteger(index) || !restoredVideos[index]) {
            throw new Error(`Restored video mapping ${String(videoEntry?.index)} is invalid.`);
        }
        if (!(videoEntry?.blob instanceof Blob)) {
            throw new Error(`Restored video ${index + 1} has no readable file bytes.`);
        }
        const sourceSize = restoredVideoSourceSize(restoredVideos[index], videoEntry);
        const mode = getStage1VideoProcessingMode(sourceSize);
        const sourceDescriptor = {
            ...restoredVideos[index],
            ...(videoEntry.meta || {}),
            size: sourceSize,
        };
        const storagePolicy = buildVideoStoragePolicy(sourceDescriptor, mode);
        const videoBlobId = await persistVideoOriginalInIndexedDbIfAllowed(
            storagePolicy,
            () => deps.storeVideoBlob(videoEntry.blob, project.id, {
                ...(videoEntry.meta || {}),
                restoredFromSyncFolder: true,
            }),
        );
        if (storagePolicy.persistOriginalInIndexedDb && !videoBlobId) {
            throw new Error(`Restored video ${index + 1} could not be stored.`);
        }
        restoredVideos[index] = {
            ...restoredVideos[index],
            size: sourceSize,
            videoBlobId,
            processingMode: mode.kind,
            processed: !mode.processInline,
            skippedLargeProcessing: !mode.processInline,
            storagePolicy,
            localSourceOnly: true,
            canonicalSource: storagePolicy.canonicalSource,
            localSourcePath: storagePolicy.sourcePath,
            syncFileName: storagePolicy.syncFileName,
            durabilityStatus: storagePolicy.durabilityStatus,
            durabilityLimitation: storagePolicy.durabilityLimitation,
        };
    }
    project.videos = restoredVideos;

    await deps.saveProject(project);

    // A folder's AI_Learnings.json is not restored: the learning loop is gone (D4).
    return { project };
}
